/**
 * 装配体配合设计树投影。
 *
 * 文件职责：
 * 1. 将 Mate Definition 列表投影为 Mates 文件夹节点；
 * 2. 只输出 UI 可消费的纯数据；
 * 3. 不读取 Three.js 对象或 web-client 状态。
 */

import type { AssemblyMateDocumentModel } from '../document';
import {
  AssemblyMateType,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import { isAssemblyMateAutoAngleReferenceEntity } from '../definition/AssemblyMateAngleReference';
import {
  areAssemblyNodeIdentityEquivalent,
  collectAssemblyNodeIdentityLookupIds,
  decodeAssemblyNodeIdSafely,
  normalizeAssemblyNodeId,
  resolveAssemblyMateOwnerAssemblyNodeId,
  splitAssemblyNodePath,
} from '../utils';

/**
 * 配合投影节点类型。
 */
export type AssemblyMateProjectionNodeKind = 'folder' | 'mate';

/**
 * 配合参与实体投影。
 *
 * 设计说明：
 * 该结构只保存 cad-core 已经持久化的稳定引用和显示摘要，供设计树、调用提示和批量命令使用。
 * web-client 不需要也不允许回查 Object3D、userData 或 BRep 来推导这些信息。
 */
export interface AssemblyMateProjectionParticipant {
  /** 实体稳定 ID。 */
  readonly stableRef: string;
  /** 所属装配节点 ID。 */
  readonly assemblyNodeId: string;
  /** 所属组件实例 ID。 */
  readonly componentInstanceId: string;
  /** SOLIDWORKS 风格组件实例短名称。 */
  readonly componentLabel: string;
  /** 选择实体在 PropertyManager 中的显示名称。 */
  readonly entityLabel: string;
  /** 配合实体语义类型。 */
  readonly entityKind: MateEntityRef['entityKind'];
}

/**
 * 配合投影节点。
 */
export interface AssemblyMateProjectionNode {
  /** 节点 ID。 */
  readonly id: string;
  /** 节点类型。 */
  readonly kind: AssemblyMateProjectionNodeKind;
  /** 展示名称。 */
  readonly label: string;
  /** 该配合文件夹所属装配体节点 ID。 */
  readonly ownerAssemblyNodeId?: string;
  /** 该 Mate in assembly 文件夹所属组件节点 ID。 */
  readonly ownerComponentNodeId?: string;
  /** Mate ID。 */
  readonly mateId?: string;
  /** Mate 类型。 */
  readonly mateType?: AssemblyMateType;
  /** 求解状态。 */
  readonly solveStatus?: MateSolveStatus;
  /** 错误信息。 */
  readonly errorMessage?: string;
  /** Mate 是否被抑制。 */
  readonly suppressed?: boolean;
  /** 同轴心配合是否锁定旋转。 */
  readonly lockRotation?: boolean;
  /** Mate 两端参与实体摘要。 */
  readonly participants?: readonly AssemblyMateProjectionParticipant[];
  /** 子节点。 */
  readonly children?: readonly AssemblyMateProjectionNode[];
}

/**
 * 单个装配体节点下的配合文件夹投影。
 */
export interface AssemblyMateAssemblyProjection {
  /** 装配体或子装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 该装配体节点下的配合文件夹。 */
  readonly folder: AssemblyMateProjectionNode;
}

/**
 * 单个组件或子装配体节点下的 Mate in assembly 文件夹投影。
 */
export interface AssemblyMateComponentProjection {
  /** 参与 Mate 的组件或子装配体节点 ID。 */
  readonly componentNodeId: string;
  /** Mate 所属的父级装配体节点 ID。 */
  readonly ownerAssemblyNodeId: string;
  /** 该组件节点下的 Mate in assembly 文件夹。 */
  readonly folder: AssemblyMateProjectionNode;
}

/**
 * 装配节点的权威显示元数据。
 *
 * 设计说明：
 * Mate Definition 只持久化稳定节点 ID，不应承担本地化名称或实例序号职责。调用方从当前
 * imported-model 层级提供显示元数据后，cad-core 统一生成全局和组件上下文标签。
 */
export interface AssemblyMateProjectionNodeDisplayEntry {
  /** 稳定装配节点 ID。 */
  readonly assemblyNodeId: string;
  /** 不含固定状态、配置和显示状态的节点名称。 */
  readonly displayName: string;
  /** SOLIDWORKS 组件实例序号。 */
  readonly instanceIndex?: number;
}

/**
 * Mate 投影内部使用的规范节点显示记录。
 */
interface ResolvedAssemblyMateProjectionNodeDisplay {
  /** 稳定装配节点 ID。 */
  readonly assemblyNodeId: string;
  /** 规范化后的节点名称。 */
  readonly displayName: string;
  /** 规范化后的实例序号。 */
  readonly instanceIndex: number;
}

/**
 * Mate 投影显示上下文。
 *
 * 该索引同时登记编码、解码和无实例路径别名，避免调用方重复实现节点 ID 兼容规则。
 */
export interface AssemblyMateProjectionDisplayContext {
  /** 装配节点 lookup ID 到权威显示元数据的映射。 */
  readonly nodeDisplayByLookupId: ReadonlyMap<string, ResolvedAssemblyMateProjectionNodeDisplay>;
  /** 用于父路径前缀差异兜底比较的去重节点显示记录。 */
  readonly nodeDisplays: readonly ResolvedAssemblyMateProjectionNodeDisplay[];
}

/**
 * Mate 类型中文名称。
 */
const MATE_TYPE_LABEL_MAP: Readonly<Record<AssemblyMateType, string>> = {
  [AssemblyMateType.Coincident]: '重合',
  [AssemblyMateType.Parallel]: '平行',
  [AssemblyMateType.Perpendicular]: '垂直',
  [AssemblyMateType.Tangent]: '相切',
  [AssemblyMateType.Concentric]: '同心',
  [AssemblyMateType.Lock]: '锁定',
  [AssemblyMateType.Distance]: '距离',
  [AssemblyMateType.Angle]: '角度',
};

/**
 * 解析 SOLIDWORKS FeatureManager 使用的 Mate 类型名称。
 *
 * @param mateType Mate 类型。
 * @returns Mate 类型显示名称。
 */
export function resolveAssemblyMateTypeDisplayLabel(mateType: AssemblyMateType): string {
  return MATE_TYPE_LABEL_MAP[mateType];
}

/**
 * 从名称末尾解析显式组件实例序号。
 *
 * @param value 原始节点名称或节点 ID 叶子。
 * @returns 合法实例序号；未携带时返回 `undefined`。
 */
function resolveExplicitComponentInstanceIndex(value: string): number | undefined {
  const decodedValue = decodeAssemblyNodeIdSafely(value);
  const match = decodedValue.match(/<(\d+)>$/u) ?? decodedValue.match(/:(\d+)$/u);
  if (!match) {
    return undefined;
  }
  const parsedIndex = Number(match[1]);
  return Number.isInteger(parsedIndex) && parsedIndex > 0 ? parsedIndex : undefined;
}

/**
 * 规范化装配节点显示名称。
 *
 * 设计说明：
 * 稳定 ID 中的 `#`、路径数组索引和旧 `:1<1>` 只用于身份定位，不属于 FeatureManager
 * 用户可见名称。即使权威层级缺失，也必须先安全解码并移除这些技术后缀。
 *
 * @param value 原始显示名称。
 * @returns 可用于 FeatureManager 的名称。
 */
function normalizeAssemblyProjectionDisplayName(value: string): string {
  const normalizedName = decodeAssemblyNodeIdSafely(value)
    .replace(/^\s*\([^)]+\)\s*/u, '')
    .replace(/<\d+>$/u, '')
    .replace(/:\d+$/u, '')
    .replace(/#\d+$/u, '')
    .replace(/\[\d+\]$/u, '')
    .trim();
  return normalizedName || '未命名组件';
}

/**
 * 创建 Mate 投影显示上下文。
 *
 * 设计说明：
 * 1. 节点 ID 的编码兼容复用装配路径统一工具；
 * 2. 同一 lookup ID 命中多个 occurrence 时不登记歧义别名，禁止同名多实例静默串名；
 * 3. 实例序号优先使用导入层明确值，其次读取名称中的 SOLIDWORKS 后缀，最后使用 1。
 *
 * @param entries 当前设计树或 imported-model 层级中的节点显示元数据。
 * @returns 可传给所有 Mate 投影入口的只读上下文。
 */
export function createAssemblyMateProjectionDisplayContext(
  entries: readonly AssemblyMateProjectionNodeDisplayEntry[],
): AssemblyMateProjectionDisplayContext {
  const nodeDisplays: ResolvedAssemblyMateProjectionNodeDisplay[] = [];
  // 完整解码身份只在输入侧解析，避免为每个新节点反复扫描、解码此前全部记录。
  const nodeDisplayByIdentity = new Map<string, ResolvedAssemblyMateProjectionNodeDisplay>();
  // null 表示已有不同 occurrence 占用该别名；无需保存所有候选并逐个 includes 查重。
  const nodeDisplayCandidatesByLookupId = new Map<string, ResolvedAssemblyMateProjectionNodeDisplay | null>();

  entries.forEach((entry) => {
    const assemblyNodeId = normalizeAssemblyNodeId(entry.assemblyNodeId).trim();
    const displayName = entry.displayName.trim();
    if (!assemblyNodeId || !displayName) {
      return;
    }
    const explicitInstanceIndex = Number.isInteger(entry.instanceIndex) && Number(entry.instanceIndex) > 0
      ? Number(entry.instanceIndex)
      : undefined;
    const decodedAssemblyNodeId = decodeAssemblyNodeIdSafely(assemblyNodeId);
    const existingNodeDisplay = nodeDisplayByIdentity.get(decodedAssemblyNodeId);
    const nodeDisplay = existingNodeDisplay ?? {
      assemblyNodeId,
      displayName: normalizeAssemblyProjectionDisplayName(displayName),
      instanceIndex: explicitInstanceIndex
        ?? resolveExplicitComponentInstanceIndex(displayName)
        ?? 1,
    };
    if (!existingNodeDisplay) {
      nodeDisplayByIdentity.set(decodedAssemblyNodeId, nodeDisplay);
      nodeDisplays.push(nodeDisplay);
    }
    const lookupIds = collectAssemblyNodeIdentityLookupIds(assemblyNodeId);
    lookupIds.forEach((lookupId) => {
      const candidate = nodeDisplayCandidatesByLookupId.get(lookupId);
      if (candidate === undefined) {
        nodeDisplayCandidatesByLookupId.set(lookupId, nodeDisplay);
      } else if (candidate !== nodeDisplay && candidate !== null) {
        // 歧义只在本次构建中单向产生，后到的重复记录不能把歧义别名恢复为唯一身份。
        nodeDisplayCandidatesByLookupId.set(lookupId, null);
      }
    });
  });
  const nodeDisplayByLookupId = new Map<string, ResolvedAssemblyMateProjectionNodeDisplay>();
  nodeDisplayCandidatesByLookupId.forEach((candidate, lookupId) => {
    if (candidate) {
      nodeDisplayByLookupId.set(lookupId, candidate);
    }
  });

  return {
    nodeDisplayByLookupId,
    nodeDisplays,
  };
}

/**
 * 解析 Mate 中需要投影到设计树的实体。
 *
 * 设计说明：
 * Angle Mate 的手动 Reference Entity 是用户选择的真实参考实体，SOLIDWORKS 会把它作为该 Mate
 * 的上下文展示；自动参考轴则是 cad-core 为稳定求解派生的内部实体，不应污染设计树组件文件夹。
 *
 * @param mate Mate Definition。
 * @returns 去重后的实体列表。
 */
function resolveProjectableMateEntities(mate: AssemblyMateDefinition): readonly MateEntityRef[] {
  const entities = [
    mate.firstEntity,
    mate.secondEntity,
    ...(mate.referenceEntity && !isAssemblyMateAutoAngleReferenceEntity(mate.referenceEntity)
      ? [mate.referenceEntity]
      : []),
  ];
  const entityByKey = new Map<string, MateEntityRef>();
  entities.forEach((entity) => {
    entityByKey.set(`${entity.componentInstanceId}:${entity.stableRef}`, entity);
  });
  return [...entityByKey.values()];
}

/**
 * 解析装配节点的权威显示记录。
 *
 * @param assemblyNodeId 装配体节点 ID。
 * @param displayContext 权威层级显示上下文。
 * @returns 权威显示记录；上下文未命中时从稳定 ID 安全派生。
 */
function resolveAssemblyProjectionNodeDisplay(
  assemblyNodeId: string,
  displayContext?: AssemblyMateProjectionDisplayContext,
): ResolvedAssemblyMateProjectionNodeDisplay {
  for (const lookupId of collectAssemblyNodeIdentityLookupIds(assemblyNodeId)) {
    const nodeDisplay = displayContext?.nodeDisplayByLookupId.get(lookupId);
    if (nodeDisplay) {
      return nodeDisplay;
    }
  }
  const equivalentNodeDisplays = displayContext?.nodeDisplays.filter((nodeDisplay) => (
    areAssemblyNodeIdentityEquivalent(nodeDisplay.assemblyNodeId, assemblyNodeId)
  )) ?? [];
  if (equivalentNodeDisplays.length === 1) {
    return equivalentNodeDisplays[0] as ResolvedAssemblyMateProjectionNodeDisplay;
  }

  const decodedAssemblyNodeId = decodeAssemblyNodeIdSafely(assemblyNodeId);
  const pathSegments = splitAssemblyNodePath(decodedAssemblyNodeId);
  const rawLeafName = pathSegments[pathSegments.length - 1] ?? decodedAssemblyNodeId;
  return {
    assemblyNodeId: normalizeAssemblyNodeId(assemblyNodeId),
    displayName: normalizeAssemblyProjectionDisplayName(rawLeafName),
    instanceIndex: resolveExplicitComponentInstanceIndex(rawLeafName) ?? 1,
  };
}

/**
 * 判断两个装配节点 ID 是否具有相同的规范 lookup 身份。
 *
 * 这里刻意不使用父路径后缀兼容：同名零件可能分别位于不同子装配体，Mate 标签去重与组件上下文
 * 排除必须保持 occurrence 精确性。父路径差异只允许在显示上下文查找中以“唯一候选”方式兜底。
 *
 * @param left 左侧装配节点 ID。
 * @param right 右侧装配节点 ID。
 * @returns 编码或解码后的完整 occurrence ID 相同时返回 `true`。
 */
function areAssemblyProjectionNodeLookupIdsEquivalent(left: string, right: string): boolean {
  const normalizedLeft = normalizeAssemblyNodeId(left);
  const normalizedRight = normalizeAssemblyNodeId(right);
  return normalizedLeft === normalizedRight
    || decodeAssemblyNodeIdSafely(normalizedLeft) === decodeAssemblyNodeIdSafely(normalizedRight);
}

/**
 * 解析装配体节点在 FeatureManager 中的短名称。
 *
 * @param assemblyNodeId 装配节点 ID。
 * @param displayContext 权威层级显示上下文。
 * @returns 不带路径和实例序号的装配体显示名称。
 */
function resolveAssemblyShortDisplayName(
  assemblyNodeId: string,
  displayContext?: AssemblyMateProjectionDisplayContext,
): string {
  return resolveAssemblyProjectionNodeDisplay(assemblyNodeId, displayContext).displayName;
}

/**
 * 解析 SOLIDWORKS 风格组件实例显示名。
 *
 * @param assemblyNodeId 装配节点 ID。
 * @param displayContext 权威层级显示上下文。
 * @returns 组件实例显示名。
 */
function resolveSolidWorksComponentDisplayName(
  assemblyNodeId: string,
  displayContext?: AssemblyMateProjectionDisplayContext,
): string {
  const nodeDisplay = resolveAssemblyProjectionNodeDisplay(assemblyNodeId, displayContext);
  return `${nodeDisplay.displayName}<${nodeDisplay.instanceIndex}>`;
}

/**
 * 生成 Mate 类型序号表。
 *
 * @returns Mate 类型序号表。
 */
function createMateTypeCounters(): Record<AssemblyMateType, number> {
  return {
    [AssemblyMateType.Coincident]: 0,
    [AssemblyMateType.Parallel]: 0,
    [AssemblyMateType.Perpendicular]: 0,
    [AssemblyMateType.Tangent]: 0,
    [AssemblyMateType.Concentric]: 0,
    [AssemblyMateType.Lock]: 0,
    [AssemblyMateType.Distance]: 0,
    [AssemblyMateType.Angle]: 0,
  };
}

/**
 * 推进 Mate 类型序号。
 *
 * @param counters 序号表。
 * @param mateType Mate 类型。
 * @returns 当前 Mate 在该类型下的序号。
 */
function advanceMateTypeCounter(
  counters: Record<AssemblyMateType, number>,
  mateType: AssemblyMateType,
): number {
  counters[mateType] += 1;
  return counters[mateType];
}

/**
 * 为 Mate 列表生成稳定的同类型序号。
 *
 * 设计说明：
 * SOLIDWORKS 中同一个 Mate 在全局 Mates 文件夹和组件 Mate in assembly 文件夹中共享同一类型序号，
 * 但括号中的参与者摘要会根据当前组件上下文变化。因此序号必须先按文档顺序统一计算，不能在每个
 * 文件夹内重新计数。
 *
 * @param mates Mate Definition 列表。
 * @returns Mate ID 到同类型序号的映射。
 */
function createMateDisplayIndexById(
  mates: readonly AssemblyMateDefinition[],
): ReadonlyMap<string, number> {
  const counters = createMateTypeCounters();
  const indexById = new Map<string, number>();
  mates.forEach((mate) => {
    indexById.set(mate.mateId, advanceMateTypeCounter(counters, mate.mateType));
  });
  return indexById;
}

/**
 * 解析 Mate 展示名称。
 *
 * @param mate Mate Definition。
 * @param index 序号。
 * @param displayContext 权威层级显示上下文。
 * @param ownerComponentNodeId 当前组件上下文节点 ID；全局名称为空。
 * @returns 展示名称。
 */
export function resolveAssemblyMateDisplayLabel(
  mate: AssemblyMateDefinition,
  index: number,
  displayContext?: AssemblyMateProjectionDisplayContext,
  ownerComponentNodeId?: string,
): string {
  const uniqueComponentNodeIds: string[] = [];
  resolveProjectableMateEntities(mate).forEach((entity) => {
    if (!uniqueComponentNodeIds.some((componentNodeId) => (
      areAssemblyProjectionNodeLookupIdsEquivalent(componentNodeId, entity.assemblyNodeId)
    ))) {
      uniqueComponentNodeIds.push(entity.assemblyNodeId);
    }
  });
  const contextualComponentNodeIds = ownerComponentNodeId
    ? uniqueComponentNodeIds.filter((componentNodeId) => (
        !areAssemblyProjectionNodeLookupIdsEquivalent(componentNodeId, ownerComponentNodeId)
      ))
    : uniqueComponentNodeIds;
  const visibleComponentNodeIds = contextualComponentNodeIds.length > 0
    ? contextualComponentNodeIds
    : uniqueComponentNodeIds;
  const componentLabels = visibleComponentNodeIds.map((componentNodeId) => (
    resolveSolidWorksComponentDisplayName(componentNodeId, displayContext)
  ));

  return `${resolveAssemblyMateTypeDisplayLabel(mate.mateType)}${index} (${componentLabels.join(',')})`;
}

/**
 * 为 Mate 列表创建设计树展示名称索引。
 *
 * 设计说明：
 * 删除确认、全局配合文件夹和组件 Mate in assembly 文件夹都必须使用同一套 SolidWorks
 * 命名规则，避免某些入口暴露 `mateId` 这类内部稳定标识。
 *
 * @param mates Mate Definition 列表。
 * @param displayContext 权威层级显示上下文。
 * @returns Mate ID 到设计树展示名称的映射。
 */
export function createAssemblyMateDisplayLabelById(
  mates: readonly AssemblyMateDefinition[],
  displayContext?: AssemblyMateProjectionDisplayContext,
): ReadonlyMap<string, string> {
  const indexById = createMateDisplayIndexById(mates);
  const labelById = new Map<string, string>();

  mates.forEach((mate) => {
    // Mate 名称序号必须基于完整文档顺序生成，不能按当前视图或删除子集重新计数。
    labelById.set(
      mate.mateId,
      resolveAssemblyMateDisplayLabel(mate, indexById.get(mate.mateId) ?? 1, displayContext),
    );
  });

  return labelById;
}

/**
 * 将 Mate 实体引用投影为设计树可消费的参与实体摘要。
 *
 * @param entity Mate 实体引用。
 * @param displayContext 权威层级显示上下文。
 * @returns 配合参与实体投影。
 */
function projectMateParticipant(
  entity: MateEntityRef,
  displayContext?: AssemblyMateProjectionDisplayContext,
): AssemblyMateProjectionParticipant {
  return {
    stableRef: entity.stableRef,
    assemblyNodeId: normalizeAssemblyNodeId(entity.assemblyNodeId),
    componentInstanceId: entity.componentInstanceId,
    componentLabel: resolveSolidWorksComponentDisplayName(entity.assemblyNodeId, displayContext),
    entityLabel: entity.label,
    entityKind: entity.entityKind,
  };
}

/**
 * 将 Mate Definition 投影为设计树节点。
 *
 * @param mate Mate Definition。
 * @param index 序号。
 * @param nodeId 当前投影出现项的节点 ID；缺省时使用 Mate 主节点 ID。
 * @param displayContext 权威层级显示上下文。
 * @param ownerComponentNodeId 当前组件上下文节点 ID；全局投影为空。
 * @returns Mate 节点。
 */
function projectMateNode(
  mate: AssemblyMateDefinition,
  index: number,
  nodeId = `assembly-mates:${mate.mateId}`,
  displayContext?: AssemblyMateProjectionDisplayContext,
  ownerComponentNodeId?: string,
): AssemblyMateProjectionNode {
  const participants = resolveProjectableMateEntities(mate)
    .map((entity) => projectMateParticipant(entity, displayContext));
  return {
    id: nodeId,
    kind: 'mate',
    label: resolveAssemblyMateDisplayLabel(mate, index, displayContext, ownerComponentNodeId),
    mateId: mate.mateId,
    mateType: mate.mateType,
    solveStatus: mate.solveStatus,
    errorMessage: mate.errorMessage,
    suppressed: mate.suppressed,
    lockRotation: mate.mateType === AssemblyMateType.Concentric
      ? mate.lockRotation === true
      : undefined,
    participants,
  };
}

/**
 * 构造配合文件夹投影节点。
 *
 * @param ownerAssemblyNodeId 所属装配体节点 ID。
 * @param children Mate 子节点。
 * @param label 文件夹显示名称。
 * @param ownerComponentNodeId Mate in assembly 文件夹所属组件节点 ID。
 * @returns 配合文件夹投影节点。
 */
function createAssemblyMateFolderNode(
  ownerAssemblyNodeId: string,
  children: readonly AssemblyMateProjectionNode[],
  label = '配合',
  ownerComponentNodeId?: string,
): AssemblyMateProjectionNode {
  return {
    id: ownerComponentNodeId
      ? `assembly-mates-in-assembly-folder:${ownerAssemblyNodeId}:${ownerComponentNodeId}`
      : `assembly-mates-folder:${ownerAssemblyNodeId}`,
    kind: 'folder',
    label,
    ownerAssemblyNodeId,
    ownerComponentNodeId,
    children,
  };
}

/**
 * 投影装配体 Mates 文件夹。
 *
 * @param document Mate 文档。
 * @param displayContext 权威层级显示上下文。
 * @returns Mates 文件夹节点。
 */
export function projectAssemblyMatesToTree(
  document: AssemblyMateDocumentModel,
  displayContext?: AssemblyMateProjectionDisplayContext,
): AssemblyMateProjectionNode {
  const indexById = createMateDisplayIndexById(document.mates);
  return {
    id: `assembly-mates-folder:${document.assemblyId}`,
    kind: 'folder',
    label: '配合',
    ownerAssemblyNodeId: normalizeAssemblyNodeId(document.assemblyId),
    children: document.mates.map((mate) => projectMateNode(
      mate,
      indexById.get(mate.mateId) ?? 1,
      undefined,
      displayContext,
    )),
  };
}

/**
 * 按装配体 / 子装配体节点投影 Mate 文件夹。
 *
 * @param document Mate 文档。
 * @param displayContext 权威层级显示上下文。
 * @returns 每个装配体节点下的 Mate 文件夹投影。
 */
export function projectAssemblyMatesByAssembly(
  document: AssemblyMateDocumentModel,
  displayContext?: AssemblyMateProjectionDisplayContext,
): readonly AssemblyMateAssemblyProjection[] {
  const indexById = createMateDisplayIndexById(document.mates);
  const childrenByAssemblyNodeId = new Map<string, AssemblyMateProjectionNode[]>();

  document.mates.forEach((mate) => {
    const ownerAssemblyNodeId = resolveAssemblyMateOwnerAssemblyNodeId({
      documentAssemblyId: document.assemblyId,
      entities: resolveProjectableMateEntities(mate),
    });
    const children = childrenByAssemblyNodeId.get(ownerAssemblyNodeId) ?? [];
    children.push(projectMateNode(
      mate,
      indexById.get(mate.mateId) ?? 1,
      undefined,
      displayContext,
    ));
    childrenByAssemblyNodeId.set(ownerAssemblyNodeId, children);
  });

  return [...childrenByAssemblyNodeId.entries()].map(([assemblyNodeId, children]) => ({
    assemblyNodeId,
    folder: createAssemblyMateFolderNode(assemblyNodeId, children),
  }));
}

/**
 * 收集单个 Mate 参与的组件或子装配体节点。
 *
 * @param mate Mate Definition。
 * @returns 去重后的参与组件节点 ID。
 */
function resolveMateParticipantComponentNodeIds(mate: AssemblyMateDefinition): readonly string[] {
  return [...new Set(resolveProjectableMateEntities(mate).map((entity) => (
    normalizeAssemblyNodeId(entity.assemblyNodeId)
  )))].filter((componentNodeId) => componentNodeId.length > 0);
}

/**
 * 按参与组件 / 子装配体节点投影 SOLIDWORKS 风格的 Mate in assembly 文件夹。
 *
 * 设计说明：
 * SOLIDWORKS 会把每个 Mate 同时加入所属装配体的 Mates 文件夹，以及参与组件或子装配体
 * 下的 Mate in assembly 文件夹。该函数只输出纯数据，web-client 负责把它挂到匹配的树节点。
 *
 * @param document Mate 文档。
 * @param displayContext 权威层级显示上下文。
 * @returns 每个参与组件节点下的 Mate in assembly 文件夹投影。
 */
export function projectAssemblyMatesByComponent(
  document: AssemblyMateDocumentModel,
  displayContext?: AssemblyMateProjectionDisplayContext,
): readonly AssemblyMateComponentProjection[] {
  const indexById = createMateDisplayIndexById(document.mates);
  const folderInputByComponentNodeId = new Map<string, {
    ownerAssemblyNodeId: string;
    children: AssemblyMateProjectionNode[];
  }>();

  document.mates.forEach((mate) => {
    const ownerAssemblyNodeId = resolveAssemblyMateOwnerAssemblyNodeId({
      documentAssemblyId: document.assemblyId,
      entities: resolveProjectableMateEntities(mate),
    });
    resolveMateParticipantComponentNodeIds(mate).forEach((componentNodeId) => {
      const existing = folderInputByComponentNodeId.get(componentNodeId) ?? {
        ownerAssemblyNodeId,
        children: [],
      };
      folderInputByComponentNodeId.set(componentNodeId, {
        ownerAssemblyNodeId: existing.ownerAssemblyNodeId,
        children: [
          ...existing.children,
          projectMateNode(
            mate,
            indexById.get(mate.mateId) ?? 1,
            `assembly-mates-in-assembly:${componentNodeId}:${mate.mateId}`,
            displayContext,
            componentNodeId,
          ),
        ],
      });
    });
  });

  return [...folderInputByComponentNodeId.entries()].map(([componentNodeId, input]) => ({
    componentNodeId,
    ownerAssemblyNodeId: input.ownerAssemblyNodeId,
    folder: createAssemblyMateFolderNode(
      input.ownerAssemblyNodeId,
      input.children,
      `${resolveAssemblyShortDisplayName(input.ownerAssemblyNodeId, displayContext)} 中的配合`,
      componentNodeId,
    ),
  }));
}
